import * as plugins from '../../00plugins.js';

import {
  DeesElement,
  customElement,
  html,
  cssManager,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { stepperDemo } from './dees-stepper.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import { zIndexRegistry } from '../../00zindex.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import type { DeesForm } from '../../00group-form/dees-form/dees-form.js';
import '../../00group-feedback/dees-progressbar/dees-progressbar.js';
import '../dees-tile/dees-tile.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { stepperStyles } from './styles.js';

export interface IStepProgressState {
  label?: string;
  percentage?: number;
  indeterminate?: boolean;
  showPercentage?: boolean;
  statusText?: string;
  terminalLines?: string[];
  statusRows?: number;
}

export interface IStepProgress extends IStepProgressState {
  autoAdvance?: boolean;
}

export interface IStep {
  title: string;
  description?: string;
  content: TemplateResult;
  allowBack?: boolean;
  progressStep?: IStepProgress;
  menuOptions?: plugins.tsclass.website.IMenuItem<DeesStepper>[];
  validationFunc?: (stepper: DeesStepper, htmlElement: HTMLElement, signal?: AbortSignal) => Promise<any>;
  onReturnToStepFunc?: (stepper: DeesStepper, htmlElement: HTMLElement) => Promise<any>;
  validationFuncCalled?: boolean;
  abortController?: AbortController;
  progressStepState?: IStepProgressState;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-stepper': DeesStepper;
  }
}

@customElement('dees-stepper')
export class DeesStepper extends DeesElement {
  // STATIC
  public static demo = stepperDemo;
  public static demoGroups = ['Layout', 'Form'];

  public static async createAndShow(optionsArg: {
    steps: IStep[];
    cancelable?: boolean;
  }): Promise<DeesStepper> {
    const body = document.body;
    const stepper = new DeesStepper();
    stepper.steps = optionsArg.steps;
    stepper.overlay = true;
    if (optionsArg.cancelable !== undefined) {
      stepper.cancelable = optionsArg.cancelable;
    }
    body.append(stepper);

    // Share overlay ordering with catalog controls opened from this dialog.
    stepper.stepperZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(stepper, stepper.stepperZIndex);
    await stepper.updateComplete;
    return stepper;
  }

  // INSTANCE
  @property({
    type: Array,
  })
  accessor steps: IStep[] = [];

  @property({
    type: Object,
  })
  accessor selectedStep!: IStep;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor overlay: boolean = false;

  /**
   * When true (default), the stepper renders a Cancel button in every step's
   * footer, and clicking the backdrop (overlay mode) triggers the same cancel
   * confirmation flow. Set to false for forced flows where the user must
   * complete the stepper — no Cancel button, no backdrop dismissal.
   */
  @property({
    type: Boolean,
    reflect: true,
  })
  accessor cancelable: boolean = true;

  @property({ type: Number, attribute: false })
  accessor stepperZIndex: number = 1000;

  @property({ type: Object, attribute: false })
  accessor activeForm: DeesForm | null = null;

  @property({ type: Boolean, attribute: false })
  accessor activeFormValid: boolean = true;

  private activeFormSubscription?: { unsubscribe: () => void };

  @state() private accessor stepError = '';
  @state() private accessor progressRunning = false;
  @state() private accessor actionPending = false;
  private resizeObserver?: ResizeObserver;
  private activeValidationStep?: IStep;
  private actionStep?: IStep;
  private destroyPromise?: Promise<void>;
  private scrollTarget?: number;
  private scrollInFlight = false;
  private cancelRequestPending = false;

  constructor() {
    super();
    this.registerStartupFunction(async (signal) => {
      await this.updateComplete;
      if (signal.aborted) return;
      this.destroyPromise = undefined;
      const dialog = this.shadowRoot?.querySelector('dialog');
      if (dialog && !dialog.open) dialog.showModal();
      this.resizeObserver = new ResizeObserver(() => this.positionSelectedStep(false));
      this.resizeObserver.observe(this.shadowRoot!.querySelector('.stepperContainer')!);
      for (const tile of this.shadowRoot!.querySelectorAll('.step')) this.resizeObserver.observe(tile);
      await this.setScrollStatus();
    });
  }

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, stepperStyles];

  public render() {
    const label = this.selectedStep?.title ?? 'Setup';
    return this.overlay
      ? html`<dialog class="stepperContainer overlay" aria-label=${label} aria-modal="true"
          style=${`z-index: ${this.stepperZIndex};`} @cancel=${this.handleDialogCancel}
          @click=${this.handleOutsideClick}>${this.renderSteps()}</dialog>`
      : html`<div class="stepperContainer" role="region" aria-label=${label}>${this.renderSteps()}</div>`;
  }

  private renderSteps() {
    if (!this.steps.length) return html`<div class="empty">No steps to show.</div>`;
    return html`
        ${this.steps.map((stepArg, stepIndex) => {
          const isSelected = stepArg === this.selectedStep;
          const isHidden =
            this.getIndexOfStep(stepArg) > this.getIndexOfStep(this.selectedStep);
          const isFirst = stepIndex === 0;
          const showBackButton = !isFirst && stepArg.allowBack !== false;
          const progressStepState = stepArg.progressStep ? this.getProgressStepState(stepArg) : null;
          return html`<dees-tile
            class="step ${isSelected ? 'selected' : ''} ${isHidden ? 'hiddenStep' : ''}"
            overscroll="contain" ?inert=${!isSelected} aria-hidden=${!isSelected}
          >
            <div slot="header" class="step-header">
              <ol class="step-rail" aria-label="Progress">${this.steps.map((step, index) => html`
                <li class="step-marker ${index < stepIndex ? 'complete' : index === stepIndex ? 'current' : ''}"
                  aria-label=${`${index + 1}. ${step.title}${index < stepIndex ? ', completed' : ''}`}
                  aria-current=${index === stepIndex ? 'step' : 'false'} title=${step.title}></li>
              `)}</ol>
              <span class="step-counter">${stepIndex + 1} of ${this.steps.length}</span>
            </div>
            <div class="step-body">
              <div class="heading-row">
                <h2 class="title" tabindex="-1">${stepArg.title}</h2>
                <span class="step-hint" aria-hidden=${!isSelected || this.activeForm === null || this.activeFormValid}
                  title="Fill in the required fields to continue.">Complete required fields</span>
              </div>
              ${stepArg.description ? html`<p class="description">${stepArg.description}</p>` : ''}
              ${stepArg.progressStep && progressStepState ? html`
                <div class="progressStep">
                  <dees-progressbar
                    .label=${progressStepState.label ?? stepArg.title}
                    .percentage=${progressStepState.percentage ?? 0}
                    .indeterminate=${progressStepState.indeterminate ?? false}
                    .showPercentage=${progressStepState.showPercentage ?? true}
                    .statusText=${progressStepState.statusText ?? ''}
                    .terminalLines=${progressStepState.terminalLines ?? []}
                    .statusRows=${progressStepState.statusRows ?? 3}
                  ></dees-progressbar>
                </div>
              ` : ''}
              <div class="content ${stepArg.progressStep ? 'withProgressStep' : ''}">${stepArg.content}</div>
              ${isSelected && this.stepError ? html`<div class="step-error" role="alert">
                <dees-icon icon="lucide:CircleAlert" iconSize="16"></dees-icon><span>${this.stepError}</span>
              </div>` : ''}
            </div>
            <div slot="footer" class="step-footer">
              <div class="bottomButtons">
              ${showBackButton ? html`<dees-button class="back-action" type="ghost" text="Back"
                icon="lucide:ChevronLeft" .disabled=${this.actionPending} @clicked=${this.goBack}></dees-button>`
                : html`<span class="footer-spacer"></span>`}
              ${this.cancelable
                ? html`<dees-button type="ghost" text="Cancel" @clicked=${this.handleCancelRequest}></dees-button>`
                : ''}
              ${isSelected && this.stepError && stepArg.validationFunc
                ? html`<dees-button type="accent" text="Try again" icon="lucide:RotateCw" @clicked=${this.retryStep}></dees-button>` : ''}
              ${stepArg.menuOptions?.map((actionArg, actionIndex) => {
                const isPrimary = actionIndex === stepArg.menuOptions!.length - 1;
                const isDisabled = this.actionPending || (isPrimary && (!this.activeFormValid || this.progressRunning || (!!this.stepError && !!stepArg.progressStep)));
                return html`<dees-button class="step-action" .text=${actionArg.name}
                  .icon=${actionArg.iconName ?? ''} .type=${isPrimary ? 'accent' : 'secondary'}
                  .disabled=${isDisabled} .status=${isPrimary && this.actionPending ? 'pending' : 'normal'}
                  @clicked=${() => this.handleMenuOptionClick(actionArg, isPrimary)}></dees-button>`;
              }) ?? ''}
              </div>
            </div>
          </dees-tile>`;
        })}
    `;
  }

  public getIndexOfStep = (stepArg: IStep): number => {
    return this.steps.findIndex((stepArg2) => stepArg === stepArg2);
  };

  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (changed.has('steps') && !this.steps.includes(this.selectedStep)) this.selectedStep = this.steps[0];
    if (changed.has('selectedStep') || changed.has('steps')) {
      if (this.activeValidationStep !== this.selectedStep) {
        this.activeValidationStep?.abortController?.abort();
        if (this.activeValidationStep) this.activeValidationStep.validationFuncCalled = false;
        this.activeValidationStep = this.selectedStep;
        this.stepError = '';
        this.progressRunning = false;
        this.actionPending = false;
        this.actionStep = undefined;
        this.prepareStepForActivation(this.selectedStep);
      }
    }
  }

  public async updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('overlay')) {
      this.scroller?.destroy();
      this.scroller = undefined!;
      this.scrollTarget = undefined;
      this.scrollInFlight = false;
    }
    const dialog = this.shadowRoot?.querySelector('dialog');
    if (dialog && !dialog.open && this.isConnected) dialog.showModal();
    if (!changedProperties.has('selectedStep') && !changedProperties.has('steps') && !changedProperties.has('overlay')) return;
    await this.setScrollStatus();
    if (!this.isConnected) return;
    if (changedProperties.has('steps') || changedProperties.has('overlay')) {
      this.resizeObserver?.disconnect();
      this.resizeObserver?.observe(this.shadowRoot!.querySelector('.stepperContainer')!);
      for (const tile of this.shadowRoot!.querySelectorAll('.step')) this.resizeObserver?.observe(tile);
    }
    if (this.overlay || this.shadowRoot?.activeElement) {
      this.shadowRoot?.querySelector<HTMLElement>('.selected .title')?.focus({ preventScroll: true });
    }
  }

  public scroller!: typeof domtools.plugins.SweetScroll.prototype;

  public async setScrollStatus() {
    const step = this.selectedStep;
    const selectedStepElement = this.shadowRoot?.querySelector<HTMLElement>('.step.selected');
    if (!selectedStepElement || !this.isConnected) return;
    const form = selectedStepElement.querySelector('dees-form');
    await form?.updateComplete;
    if (!this.isConnected || step !== this.selectedStep) return;
    this.scanActiveForm(selectedStepElement);
    this.positionSelectedStep(true);
    if (!step.validationFuncCalled && step.validationFunc) {
      step.abortController = new AbortController();
      step.validationFuncCalled = true;
      void this.runStepValidation(step, selectedStepElement, step.abortController.signal);
    }
  }

  private positionSelectedStep(animate: boolean) {
    const container = this.shadowRoot?.querySelector<HTMLElement>('.stepperContainer');
    const selected = this.shadowRoot?.querySelector<HTMLElement>('.step.selected');
    const first = this.shadowRoot?.querySelector<HTMLElement>('.step');
    if (!container || !selected || !first || !this.isConnected) return;
    container.style.paddingTop = `${Math.max(24, (container.clientHeight - first.offsetHeight) / 2)}px`;
    const position = Math.max(0, selected.offsetTop - (container.clientHeight - selected.offsetHeight) / 2);
    if (!this.scroller) {
      this.scroller = new domtools.plugins.SweetScroll(
        { vertical: true, horizontal: false, easing: 'easeOutCubic', duration: 320, cancellable: false },
        container
      );
      animate = false;
    }
    // Form validity can resize the footer after selection. Retarget the active
    // scroll instead of letting ResizeObserver snap it to the destination.
    if (this.scrollTarget !== undefined && Math.abs(this.scrollTarget - position) < 1) return;
    const duration = (animate || this.scrollInFlight) && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 320 : 0;
    this.scrollTarget = position;
    this.scroller.to(position, { duration, complete: () => { this.scrollInFlight = false; } });
    this.scrollInFlight = duration > 0;
  }

  public async goBack() {
    const currentIndex = this.steps.findIndex((stepArg) => stepArg === this.selectedStep);
    if (currentIndex <= 0 || this.selectedStep.allowBack === false) {
      return;
    }
    // Abort any active listeners on current step
    if (this.selectedStep.abortController) {
      this.selectedStep.abortController.abort();
    }
    const currentStep = this.steps[currentIndex];
    currentStep.validationFuncCalled = false;
    const previousStep = this.steps[currentIndex - 1];
    previousStep.validationFuncCalled = false;
    this.prepareStepForActivation(previousStep);
    this.selectedStep = previousStep;
    await this.updateComplete;
    if (this.isConnected && this.selectedStep === previousStep) {
      try { await previousStep.onReturnToStepFunc?.(this, this.shadowRoot!.querySelector('.selected') as HTMLElement); }
      catch (error) { if (this.selectedStep === previousStep) this.stepError = error instanceof Error ? error.message : 'Could not return to this step.'; }
    }
  }

  public goNext() {
    const currentIndex = this.steps.findIndex((stepArg) => stepArg === this.selectedStep);
    if (currentIndex < 0 || currentIndex >= this.steps.length - 1) {
      return;
    }
    // Abort any active listeners on current step
    if (this.selectedStep.abortController) {
      this.selectedStep.abortController.abort();
    }
    const currentStep = this.steps[currentIndex];
    currentStep.validationFuncCalled = false;
    const nextStep = this.steps[currentIndex + 1];
    nextStep.validationFuncCalled = false;
    this.prepareStepForActivation(nextStep);
    this.selectedStep = nextStep;
  }

  public resetProgressStep(stepArg: IStep = this.selectedStep) {
    if (!stepArg?.progressStep) {
      return;
    }

    stepArg.progressStepState = this.createInitialProgressStepState(stepArg);
    this.requestUpdate();
  }

  public updateProgressStep(
    progressStateArg: Partial<IStepProgressState>,
    stepArg: IStep = this.selectedStep,
  ) {
    if (!stepArg?.progressStep) {
      return;
    }

    const currentProgressState = this.getProgressStepState(stepArg);
    stepArg.progressStepState = {
      ...currentProgressState,
      ...progressStateArg,
      terminalLines: progressStateArg.terminalLines
        ? [...progressStateArg.terminalLines]
        : [...(currentProgressState.terminalLines ?? [])],
    };
    this.requestUpdate();
  }

  public appendProgressStepLine(lineArg: string, stepArg: IStep = this.selectedStep) {
    if (!stepArg?.progressStep) {
      return;
    }

    const currentProgressState = this.getProgressStepState(stepArg);
    this.updateProgressStep(
      {
        terminalLines: [...(currentProgressState.terminalLines ?? []), lineArg],
      },
      stepArg,
    );
  }

  /**
   * Scans the currently selected step for a <dees-form> in its content. When
   * found, subscribes to the form's RxJS changeSubject so the primary
   * menuOption button can auto-enable/disable as required fields are filled.
   *
   * If the form reference is the same as the previous activation (e.g. on a
   * same-step re-render), we just recompute validity without re-subscribing.
   */
  private scanActiveForm(selectedStepElement: HTMLElement) {
    const form = selectedStepElement.querySelector('dees-form') as DeesForm | null;
    if (form === this.activeForm) {
      this.recomputeFormValid();
      return;
    }
    this.activeFormSubscription?.unsubscribe();
    this.activeFormSubscription = undefined;
    this.activeForm = form;
    if (!form) {
      this.activeFormValid = true;
      return;
    }
    // Initial check before subscribing, in case the form's firstUpdated fires
    // synchronously between scan and subscribe.
    this.recomputeFormValid();
    this.activeFormSubscription = form.changeSubject.subscribe(() => {
      this.recomputeFormValid();
    });
  }

  /**
   * Recomputes activeFormValid by checking every required field in the active
   * form for a non-empty value. Mirrors dees-form.updateRequiredStatus's logic
   * but stores the result on the stepper instead of mutating a submit button.
   */
  private recomputeFormValid() {
    const form = this.activeForm;
    if (!form) {
      this.activeFormValid = true;
      return;
    }
    const fields = form.getFormElements();
    this.activeFormValid = fields.every(field => {
      if (!field.required || field.disabled) return true;
      const value = 'getValue' in field && field.getValue ? field.getValue() : field.value;
      return value !== null && value !== undefined && value !== '' && value !== false && (!Array.isArray(value) || value.length > 0);
    });
  }

  /**
   * Click handler for menuOption buttons in the footer. For the primary (last)
   * button, if an active form is present, gates on required-field validity and
   * triggers the form's gatherAndDispatch() before running the action. The
   * action is awaited so any async work (e.g. goNext → scroll animation)
   * completes before the click handler returns.
   */
  private async handleMenuOptionClick(
    optionArg: plugins.tsclass.website.IMenuItem<DeesStepper>,
    isPrimary: boolean,
  ) {
    if (this.actionPending || (isPrimary && this.progressRunning)) return;
    this.recomputeFormValid();
    const form = this.activeForm;
    if (isPrimary && !this.activeFormValid) return;
    const step = this.selectedStep;
    this.actionPending = true;
    this.actionStep = step;
    this.stepError = '';
    try {
      if (isPrimary && form) await form.gatherAndDispatch();
      if (!this.isConnected || step !== this.selectedStep) return;
      await optionArg.action(this);
    } catch (error) {
      if (this.isConnected && step === this.selectedStep) this.stepError = error instanceof Error ? error.message : 'The action could not be completed. Try again.';
    } finally {
      if (this.actionStep === step) { this.actionPending = false; this.actionStep = undefined; }
    }
  }

  private getProgressStepState(stepArg: IStep): IStepProgressState {
    if (!stepArg.progressStep) {
      return {};
    }

    return stepArg.progressStepState ?? this.createInitialProgressStepState(stepArg);
  }

  private createInitialProgressStepState(stepArg: IStep): IStepProgressState {
    return {
      label: stepArg.progressStep?.label ?? stepArg.title,
      percentage: stepArg.progressStep?.percentage ?? 0,
      indeterminate: stepArg.progressStep?.indeterminate ?? false,
      showPercentage: stepArg.progressStep?.showPercentage ?? true,
      statusText: stepArg.progressStep?.statusText ?? '',
      terminalLines: [...(stepArg.progressStep?.terminalLines ?? [])],
      statusRows: stepArg.progressStep?.statusRows ?? 3,
    };
  }

  private prepareStepForActivation(stepArg?: IStep) {
    if (!stepArg?.progressStep) {
      return;
    }

    stepArg.progressStepState = this.createInitialProgressStepState(stepArg);
  }

  private async runStepValidation(
    stepArg: IStep,
    selectedStepElement: HTMLElement,
    signal: AbortSignal,
  ): Promise<void> {
    this.progressRunning = !!stepArg.progressStep;
    try {
      await stepArg.validationFunc?.(this, selectedStepElement, signal);

      if (signal.aborted || !this.isConnected || this.selectedStep !== stepArg) {
        return;
      }

      if (stepArg.progressStep && stepArg.progressStep.autoAdvance !== false && this.selectedStep === stepArg) {
        this.goNext();
      }
    } catch (error) {
      if (signal.aborted || !this.isConnected || this.selectedStep !== stepArg) {
        return;
      }

      const errorText = error instanceof Error ? error.message : 'This step could not be completed. Try again.';
      this.stepError = errorText;
      if (stepArg.progressStep) {
        this.appendProgressStepLine(`Error: ${errorText}`, stepArg);
        this.updateProgressStep(
          {
            indeterminate: false,
            statusText: errorText,
          },
          stepArg,
        );
      }

    } finally {
      if (!signal.aborted && this.selectedStep === stepArg) this.progressRunning = false;
    }
  }

  /** Rerun the current step after a recoverable validation/provisioning failure. */
  public async retryStep() {
    const step = this.selectedStep;
    if (!step?.validationFunc || this.progressRunning || this.actionPending) return;
    step.abortController?.abort();
    step.validationFuncCalled = false;
    this.stepError = '';
    this.prepareStepForActivation(step);
    await this.setScrollStatus();
  }

  private handleDialogCancel(event: Event) {
    event.preventDefault();
    void this.handleCancelRequest();
  }

  /**
   * Currently-open confirmation modal (if any). Prevents double-stacking when
   * the user clicks the backdrop or the Cancel button while a confirm modal
   * is already visible. The reference may become stale (point to a destroyed
   * modal) if the user dismisses the confirm modal by clicking its own
   * backdrop — so handleCancelRequest() uses isConnected to detect that.
   */
  private cancelConfirmModal?: DeesModal;

  /**
   * Click handler on .stepperContainer. Mirrors dees-modal.handleOutsideClick:
   * when the user clicks the empty backdrop area (target === stepperContainer,
   * not any descendant tile), trigger the cancel confirmation flow. Clicks
   * that originate inside a step tile have a different event.target and are
   * ignored here.
   */
  private handleOutsideClick(eventArg: MouseEvent) {
    if (!this.overlay) return;
    if (!this.cancelable) return;
    eventArg.stopPropagation();
    const stepperContainer = this.shadowRoot!.querySelector('.stepperContainer');
    if (eventArg.target === stepperContainer) {
      this.handleCancelRequest();
    }
  }

  /**
   * Shown by both the backdrop click and the Cancel button in the footer.
   * Presents a dees-modal asking the user to confirm cancellation. If they
   * confirm, the stepper and window layer are destroyed; otherwise the
   * confirm modal is dismissed and the stepper stays open.
   *
   * The isConnected check on the cached reference handles the case where the
   * user dismissed the previous confirm modal by clicking ITS OWN backdrop —
   * dees-modal.handleOutsideClick calls destroy() directly, bypassing our
   * action callbacks, so our cached reference would be stale without this
   * fallback check.
   */
  public async handleCancelRequest() {
    if (!this.cancelable || this.cancelRequestPending || !this.isConnected) return;
    if (this.cancelConfirmModal && this.cancelConfirmModal.isConnected) return;
    this.cancelConfirmModal = undefined;
    this.cancelRequestPending = true;
    try { this.cancelConfirmModal = await DeesModal.createAndShow({
      heading: 'Cancel setup?',
      width: 'small',
      content: html`
        <p style="margin: 0;">
          Are you sure you want to cancel? Any progress on the current step will be lost.
        </p>
      `,
      menuOptions: [
        {
          name: 'Continue setup',
          action: async (modal) => {
            this.cancelConfirmModal = undefined;
            await modal!.destroy();
          },
        },
        {
          name: 'Yes, cancel',
          action: async (modal) => {
            this.cancelConfirmModal = undefined;
            await modal!.destroy();
            await this.destroy();
          },
        },
      ],
    });
    if (!this.isConnected) await this.cancelConfirmModal.destroy();
    } finally { this.cancelRequestPending = false; }
  }

  public destroy(): Promise<void> {
    return this.destroyPromise ??= this.closeAndRemove();
  }

  private async closeAndRemove() {
    this.selectedStep?.abortController?.abort();
    // Close nested dialogs first so the browser can restore the original opener.
    if (this.cancelConfirmModal?.isConnected) await this.cancelConfirmModal.destroy();
    this.shadowRoot?.querySelector('.stepperContainer')?.classList.add('predestroy');
    const tile = this.shadowRoot?.querySelector('.step.selected');
    if (tile) await Promise.allSettled(tile.getAnimations().map(animation => animation.finished));
    this.shadowRoot?.querySelector('dialog')?.close();
    this.remove();
  }

  public async disconnectedCallback() {
    this.selectedStep?.abortController?.abort();
    if (this.selectedStep) this.selectedStep.validationFuncCalled = false;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.scroller?.destroy();
    this.scroller = undefined!;
    this.scrollTarget = undefined;
    this.scrollInFlight = false;
    this.shadowRoot?.querySelector('dialog')?.close();
    if (this.cancelConfirmModal?.isConnected) void this.cancelConfirmModal.destroy();
    this.activeFormSubscription?.unsubscribe();
    this.activeFormSubscription = undefined;
    this.activeForm = null;
    this.actionPending = false;
    this.actionStep = undefined;
    this.progressRunning = false;
    zIndexRegistry.unregister(this);
    this.dispatchEvent(new CustomEvent('stepper-close', { bubbles: true, composed: true }));
    await super.disconnectedCallback();
  }
}
